import type React from 'react'
import { PINK } from '../lib/theme'

// Home's stylesheet. Pure data, lifted out of the screen file so the
// component itself is navigable. No logic here.

export const styles: Record<string, React.CSSProperties> = {
  page: {
    // No outer padding — App shell already provides 40px 48px (App.tsx <main>).
    margin: '0 auto',
    display: 'flex', flexDirection: 'column', gap: 28,
  },
  loading: { padding: 40, textAlign: 'center', color: 'var(--text-secondary)' },

  headerBlock: { marginBottom: 0 },
  greeting: {
    fontSize: 26, fontWeight: 700, color: 'var(--text-primary)',
    margin: 0, letterSpacing: '-0.02em',
  },
  subGreeting: { fontSize: 14, color: 'var(--text-secondary)', marginTop: 4 },

  // Section headers
  sectionHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 15, fontWeight: 600, color: 'var(--text-primary)',
    margin: 0, letterSpacing: '-0.01em',
  },
  sectionAction: {
    fontSize: 12, fontWeight: 600, color: PINK,
    background: 'none', border: 'none', cursor: 'pointer', padding: 0,
    display: 'inline-flex', alignItems: 'center', gap: 4,
  },

  // CTA card
  ctaCard: {
    background: `linear-gradient(135deg, ${PINK} 0%, #c020a0 100%)`,
    borderRadius: 14, padding: '24px 28px',
  },
  ctaTitle: { fontSize: 20, fontWeight: 800, color: '#fff', margin: '0 0 8px', letterSpacing: '-0.02em' },
  ctaDesc: { fontSize: 14, color: '#fff', fontWeight: 500, margin: '0 0 20px', lineHeight: 1.5, opacity: 0.95 },
  ctaBtnPrimary: {
    padding: '11px 22px', fontSize: 14, fontWeight: 700,
    background: '#fff', color: '#0a0a0f', border: 'none',
    borderRadius: 999, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 8,
  },

  // State 2 — empty hero, "create your first campaign"
  heroEmpty: {
    background: 'var(--bg-primary)', border: '1px dashed var(--border-primary)',
    borderRadius: 14, padding: '48px 28px', textAlign: 'center' as const,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
  },
  heroIconCircle: {
    width: 60, height: 60, borderRadius: '50%',
    background: 'rgba(251, 58, 174, 0.10)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    marginBottom: 4,
  },
  heroEmptyTitle: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', margin: 0, letterSpacing: '-0.02em' },
  heroEmptyDesc: { fontSize: 14, color: 'var(--text-secondary)', margin: '0 0 6px', maxWidth: 460, lineHeight: 1.5 },

  // "Since you last visited" card
  sinceCard: {
    background: 'var(--bg-primary)', border: '1px solid var(--border-primary)',
    borderRadius: 12, padding: '16px 20px',
  },
  sinceLabel: {
    fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)',
    textTransform: 'uppercase' as const, letterSpacing: '0.06em', marginBottom: 12,
  },
  sinceRow: { display: 'flex', gap: 32, flexWrap: 'wrap' as const },
  sinceStat: { display: 'flex', alignItems: 'center', gap: 8 },
  sinceValue: { fontSize: 22, fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.02em' },
  sinceStatLabel: { fontSize: 13, color: 'var(--text-secondary)' },

  // Recent videos strip — fluid 6-column grid so the row always fills the
  // full content width regardless of viewport (fixed 160px flex tiles left
  // dead space on wide screens). We fetch at most 6 videos, so the
  // responsive column counts (home.css: 3 at ≤1000px, 2 at ≤600px) always
  // produce even rows. minmax(0,1fr) lets tiles shrink below their
  // content's intrinsic width instead of overflowing the page.
  videoStrip: {
    display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 12,
  },
  videoTile: {
    background: 'var(--bg-primary)', border: '1px solid var(--border-primary)',
    borderRadius: 10, padding: 10,
    display: 'flex', flexDirection: 'column', gap: 8,
    minWidth: 0,
  },
  videoMeta: { display: 'flex', flexDirection: 'column', gap: 2 },
  videoHandle: {
    fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
    overflow: 'hidden' as const, textOverflow: 'ellipsis' as const, whiteSpace: 'nowrap' as const,
  },
  // Caption text: bumped from 11/400 → 12/500 per 2026-05-02 readability
  // pass. 11px secondary text rendered as mush at this contrast level.
  // Going slightly larger AND adding weight reads cleanly while staying
  // visually subordinate to the handle above.
  videoStatLine: { fontSize: 12, fontWeight: 500, color: 'var(--text-secondary)' },

  // Campaign cards on Home
  campaignGrid: { display: 'flex', flexDirection: 'column', gap: 10 },
  campaignCard: {
    background: 'var(--bg-primary)', border: '1px solid var(--border-primary)',
    borderRadius: 12, padding: 16, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 14,
  },
  campaignThumbWrap: { flexShrink: 0 },
  campaignThumb: {
    width: 56, height: 56, borderRadius: 8, objectFit: 'cover' as const,
    background: '#0a0a0f',
  },
  campaignThumbFallback: {
    width: 56, height: 56, borderRadius: 8,
    background: 'var(--bg-secondary)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  campaignName: {
    fontSize: 15, fontWeight: 700, color: 'var(--text-primary)',
    overflow: 'hidden' as const, textOverflow: 'ellipsis' as const, whiteSpace: 'nowrap' as const,
  },
  // Subtitle text under campaign name: 12 → 13, weight 400 → 500. Same
  // readability fix as videoStatLine — small secondary text needs the
  // extra weight to render crisply against the dark surface.
  campaignProduct: { fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)', marginTop: 2 },
  campaignStats: {
    display: 'flex', gap: 8, marginTop: 8,
    fontSize: 13, fontWeight: 500, color: 'var(--text-secondary)',
  },

  // State 3 nudge — "find creators"
  nudgeCard: {
    marginTop: 12,
    background: 'rgba(251, 58, 174, 0.06)',
    border: '1px solid rgba(251, 58, 174, 0.20)',
    borderRadius: 10, padding: '12px 16px',
    display: 'flex', alignItems: 'center', gap: 12,
    fontSize: 13, color: 'var(--text-primary)',
  },
  nudgeBtn: {
    padding: '7px 14px', fontSize: 13, fontWeight: 600,
    background: PINK, color: '#fff', border: 'none', borderRadius: 999, cursor: 'pointer',
    display: 'inline-flex', alignItems: 'center', gap: 6,
  },
}
